import React, { useState, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '@/redux/store';
import { doUpdateCarrier } from '../../../modules/carriers/stores/thunk';
import { doGetCarrier } from '../../../modules/carriers/stores/thunk';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Card, CardContent } from '../../ui/card';
import { Upload, X, Image as ImageIcon } from 'lucide-react';
import { useToast } from '../../../hooks/use-toast';

interface CarrierLogoModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  carrierId: number;
  currentLogo?: string;
  carrierName: string;
}

export const CarrierLogoModal: React.FC<CarrierLogoModalProps> = ({
  open,
  onOpenChange,
  carrierId,
  currentLogo,
  carrierName,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { toast } = useToast();
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);

  const handleFileSelect = useCallback(
    (selectedFile: File) => {
      const isLt5M = selectedFile.size / 1024 / 1024 <= 5;
      if (!isLt5M) {
        toast({
          title: 'Error',
          description: 'Image must be smaller than 5MB!',
          variant: 'destructive',
        });
        return;
      }

      if (!selectedFile.type.startsWith('image/')) {
        toast({
          title: 'Error',
          description: 'Please select a valid image file.',
          variant: 'destructive',
        });
        return;
      }

      setFile(selectedFile);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result as string);
      };
      reader.readAsDataURL(selectedFile);
    },
    [toast],
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragOver(false);

      const droppedFile = e.dataTransfer.files[0];
      if (droppedFile) {
        handleFileSelect(droppedFile);
      }
    },
    [handleFileSelect],
  );

  const handleFileInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const selectedFile = e.target.files?.[0];
      if (selectedFile) {
        handleFileSelect(selectedFile);
      }
    },
    [handleFileSelect],
  );

  const clearFile = useCallback(() => {
    setFile(null);
    setPreview(null);
  }, []);

  const handleSave = async () => {
    if (!file) {
      toast({
        title: 'Error',
        description: 'Please select an image to upload.',
        variant: 'destructive',
      });
      return;
    }

    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append('id', carrierId.toString());
      formData.append('logo', file);

      await dispatch(doUpdateCarrier(formData));

      // Refresh carrier data to get updated logo
      await dispatch(doGetCarrier({ id: carrierId }));

      toast({
        title: 'Success',
        description: 'Carrier logo updated successfully.',
        variant: 'default',
      });

      onOpenChange(false);
      clearFile();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to update carrier logo. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handleCancel = () => {
    clearFile();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Update Carrier Logo</DialogTitle>
          <DialogDescription>
            Upload a new logo for {carrierName}. Supported formats: PNG, JPG, JPEG. Maximum size: 5MB.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Current Logo Preview */}
          {currentLogo && (
            <div className="space-y-2">
              <p className="text-sm font-medium text-muted-foreground">Current Logo</p>
              <div className="h-16 w-16 rounded-lg bg-muted flex items-center justify-center overflow-hidden">
                <img src={currentLogo} alt="Current logo" className="h-full w-full object-cover" />
              </div>
            </div>
          )}

          {/* File Upload Area */}
          <div className="space-y-2">
            <p className="text-sm font-medium text-muted-foreground">New Logo</p>
            <Card
              className={`border-2 border-dashed transition-colors ${
                isDragOver ? 'border-primary bg-primary/5' : 'border-muted-foreground/25'
              }`}
            >
              <CardContent className="p-6">
                {!preview ? (
                  <div
                    className="flex flex-col items-center justify-center space-y-4"
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                  >
                    <div className="flex h-12 w-12 items-center justify-center rounded-full bg-muted">
                      <Upload className="h-6 w-6 text-muted-foreground" />
                    </div>
                    <div className="text-center">
                      <p className="text-sm font-medium">
                        Drag and drop an image here, or{' '}
                        <label className="cursor-pointer text-primary hover:text-primary/80">
                          browse
                          <input type="file" accept="image/*" className="hidden" onChange={handleFileInputChange} />
                        </label>
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">PNG, JPG, JPEG up to 5MB</p>
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center space-y-4">
                    <div className="relative">
                      <img src={preview} alt="Preview" className="h-24 w-24 rounded-lg object-cover" />
                      <Button
                        variant="destructive"
                        size="icon"
                        className="absolute -top-2 -right-2 h-6 w-6"
                        onClick={clearFile}
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    </div>
                    <p className="text-sm text-muted-foreground">{file?.name}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleCancel} disabled={isUploading}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={!file || isUploading}>
            {isUploading ? 'Uploading...' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
